import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Button,
  Badge,
} from '@etherisc/ui-kit';
import { 
  FileTextIcon, 
  FolderIcon, 
  BarChartIcon, 
  HistoryIcon,
  TrendingUpIcon,
  AlertTriangleIcon,
} from 'lucide-react';
import { useLedgerJournals } from '../../hooks/ledger/useLedgerJournals';
import { useLedgerReports } from '../../hooks/ledger/useLedgerReports';
import { useClientReceivablesAging } from '../../hooks/insurance/useInsuranceReporting';
import { usePaymentBatches } from '../../hooks/insurance/useInsuranceBatch';
import { PermissionGate } from '../../components/PermissionGate';
import { formatDate, formatCurrency } from '../../utils/formatters';

export function LedgerDashboard() {
  const navigate = useNavigate();
  const { journals, total: totalJournals } = useLedgerJournals();
  const { periods, chartOfAccounts } = useLedgerReports();
  
  // Insurance-specific data
  const { data: agingData } = useClientReceivablesAging();
  const { data: batchData } = usePaymentBatches({ limit: 10 });

  // Calculate dashboard metrics
  const postedJournals = journals.filter(j => j.status === 'POSTED').length;
  const draftJournals = journals.filter(j => j.status === 'DRAFT').length;
  const reversedJournals = journals.filter(j => j.status === 'REVERSED').length;
  
  const openPeriods = periods?.filter((p: unknown) => (p as { status: string }).status === 'OPEN') || [];
  const totalAccounts = chartOfAccounts?.length || 0;
  const activeAccounts = chartOfAccounts?.filter((a: unknown) => (a as { isActive: boolean }).isActive).length || 0;

  // Recent journals (last 5)
  const recentJournals = journals.slice(0, 5);

  // Insurance metrics calculations
  const totalOutstanding = agingData?.reduce((sum, entry) => 
    sum + parseFloat(entry.outstandingAmount), 0) || 0;
  
  const clientsWithOutstanding = agingData?.length || 0;
  
  const pendingBatches = batchData?.batches?.filter(b => b.status === 'pending').length || 0;
  const reconciledToday = batchData?.batches?.filter(b => 
    b.status === 'reconciled' && 
    b.reconciledDate && 
    new Date(b.reconciledDate).toDateString() === new Date().toDateString()
  ).length || 0;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Ledger Dashboard</h1>
        <p className="text-muted-foreground">
          Overview of your accounting system
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Journals</CardTitle>
            <FileTextIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalJournals}</div>
            <p className="text-xs text-muted-foreground">
              {postedJournals} posted, {draftJournals} draft, {reversedJournals} reversed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Chart of Accounts</CardTitle>
            <FolderIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalAccounts}</div>
            <p className="text-xs text-muted-foreground">
              {activeAccounts} active accounts
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Periods</CardTitle>
            <HistoryIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{openPeriods.length}</div>
            <p className="text-xs text-muted-foreground">
                  {openPeriods.map((p: unknown) => (p as { name: string }).name).join(', ') || 'None'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">System Status</CardTitle>
            <TrendingUpIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <Badge variant="success">Healthy</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              All systems operational
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Insurance Metrics */}
      {(totalOutstanding > 0 || pendingBatches > 0) && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Insurance Premium Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Premium Receivables */}
            {totalOutstanding > 0 && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Premium Receivables</CardTitle>
                  <TrendingUpIcon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(totalOutstanding, 'EUR')}</div>
                  <p className="text-xs text-muted-foreground">
                    {clientsWithOutstanding} clients with outstanding premiums
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Payment Processing */}
            {(pendingBatches > 0 || reconciledToday > 0) && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Payment Processing</CardTitle>
                  <BarChartIcon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-lg font-semibold">{pendingBatches}</div>
                      <div className="text-xs text-muted-foreground">Pending Batches</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{reconciledToday}</div>
                      <div className="text-xs text-muted-foreground">Reconciled Today</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Top Outstanding Clients */}
            {agingData && agingData.length > 0 && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Top Outstanding Clients</CardTitle>
                  <AlertTriangleIcon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {agingData.slice(0, 3).map(client => (
                      <div key={`${client.clientId}:${client.costCenterId}`} className="flex justify-between items-center text-sm">
                        <span className="truncate">{client.clientId.substring(0, 8)}...</span>
                        <span className="font-semibold">{formatCurrency(client.outstandingAmount, client.currency)}</span>
                      </div>
                    ))}
                    {agingData.length > 3 && (
                      <div className="text-xs text-muted-foreground pt-1">
                        +{agingData.length - 3} more clients
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Alerts */}
      {openPeriods.length === 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <AlertTriangleIcon className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="font-semibold text-yellow-800">No Open Periods</p>
                <p className="text-sm text-yellow-700">
                  There are no open accounting periods. You need an open period to post new journal entries.
                </p>
              </div>
              <PermissionGate permission="ledger:admin">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/ledger/periods')}
                >
                  Manage Periods
                </Button>
              </PermissionGate>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Journals */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Recent Journals</CardTitle>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/ledger/journals')}
              >
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {recentJournals.length > 0 ? (
              <div className="space-y-3">
                {recentJournals.map((journal: unknown) => {
                  const j = journal as { id: string; externalRef: string; description: string; status: string; totalAmount: string; journalDate: string };
                  return (
                  <div key={j.id} className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <p className="font-medium">{j.externalRef}</p>
                      <p className="text-sm text-muted-foreground truncate max-w-xs">
                        {j.description}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(j.journalDate)}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge variant={
                        j.status === 'POSTED' ? 'success' : 
                        j.status === 'REVERSED' ? 'destructive' : 'secondary'
                      }>
                        {j.status}
                      </Badge>
                      <p className="text-sm font-mono mt-1">
                        {formatCurrency(parseFloat(j.totalAmount))}
                      </p>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No journals found
              </p>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3">
              <Button 
                variant="outline" 
                className="justify-start"
                onClick={() => navigate('/ledger/journals')}
              >
                <FileTextIcon className="h-4 w-4 mr-2" />
                View All Journals
              </Button>
              
              <PermissionGate permission="ledger:reports">
                <Button 
                  variant="outline" 
                  className="justify-start"
                  onClick={() => navigate('/ledger/reports/trial-balance')}
                >
                  <BarChartIcon className="h-4 w-4 mr-2" />
                  Generate Trial Balance
                </Button>
              </PermissionGate>

              <Button 
                variant="outline" 
                className="justify-start"
                onClick={() => navigate('/ledger/accounts')}
              >
                <FolderIcon className="h-4 w-4 mr-2" />
                Chart of Accounts
              </Button>

              <PermissionGate permission="ledger:reports">
                <Button 
                  variant="outline" 
                  className="justify-start"
                  onClick={() => navigate('/insurance/analytics')}
                >
                  <TrendingUpIcon className="h-4 w-4 mr-2" />
                  Analytics Dashboard
                </Button>
              </PermissionGate>

              <PermissionGate permission="ledger:admin">
                <Button 
                  variant="outline" 
                  className="justify-start"
                  onClick={() => navigate('/ledger/periods')}
                >
                  <HistoryIcon className="h-4 w-4 mr-2" />
                  Manage Periods
                </Button>
              </PermissionGate>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
